//import 'react-app-polyfill/ie11'
//import 'react-app-polyfill/stable'
import { createRoot } from 'react-dom/client'
import dotenv from 'dotenv'
import { InMemoryCache, IntrospectionFragmentMatcher, ApolloClient, ApolloLink, split } from 'apollo-boost'
import { getMainDefinition } from 'apollo-utilities'
import { createHttpLink } from "apollo-link-http"
import { onError } from 'apollo-link-error'
import { fromPromise } from 'apollo-link'
import { ApolloProvider } from '@apollo/react-hooks'
import * as serviceWorker from './serviceWorker'
import { createStore } from 'redux'
import { Provider } from 'react-redux'
import rootReducer from './reducer'
import 'index.css'
import 'animate.css'
import App from './App'
import AppErrorBoundary from 'ccomponent/AppErrorBoundary'
import fragment from './fragment.json'
import { cookies, getTimezoneLang } from 'utils'
import { bootstrapSession, isRespondHost, openRelogin, waitForLogin, getMe, fetchMe, isUnauthenticated, authHeaders } from 'ts-utils/session'
import { createTheme, ThemeProvider  } from '@material-ui/core/styles'

// material-ui typography
const theme = createTheme({
	typography: {
		fontFamily: ['Roboto', 'MalgunGothic', 'sans-serif'].join(','),
		button: {
			textTransform: 'none'
		}
	}
})

const { timearea } = getTimezoneLang()

// Logined 값이 없으면 로그인 안된다.
// 브라우저 창 닫으면 로그인 해제를 위함이다. (업데이트 전 localStorage 토큰 정리용, 로그인은 쿠키 세션)
if (cookies.get('Logined') !== 'true') {
	localStorage.removeItem('authorization')
}

dotenv.config({path: `./.env.${process.env.NODE_ENV}`})

const store = process.env.NODE_ENV === 'development' ? createStore(rootReducer) : createStore(rootReducer)

// 로그인은 HttpOnly 쿠키. 응답 도메인에서는 쿠키를 보내지 않는다 (응답은 로그인과 무관)
const uploadLink = createHttpLink({
    uri: `${process.env.REACT_APP_API}/graphql`,
    credentials: isRespondHost() ? 'omit' : 'include'
})

let wsLink = () => {}


/* 
split 함수는 세 가지 매개 변수를 사용
* 실행할 각 작업에 대해 호출되는 함수
* 함수가 "truthy"값을 반환하는 경우 작업에 사용할 링크
* 함수가 "거짓"값을 반환하는 경우 작업에 사용할 링크
*/
const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query)
		return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
	},
	wsLink,
	uploadLink
)

const authLink = new ApolloLink(( operation, forward ) => {

	const lang = cookies.get('NEXT_LOCALE')
	
	operation.setContext({
		headers: {
			...authHeaders(),
			lang,
			timearea
		}
	})

	return forward(operation)
})

// 로그인/로그아웃 요청 자체는 모달 대상이 아니다
const SKIP_RELOGIN_OPERATIONS = ['Me', 'Logout', 'LoginEncrypt', 'LoginPassword', 'LoginOtp', 'MigrateSession', 'AdminHandoffLogin']

// 다른 에러로 보이지만 실제로는 로그인이 풀린 경우를 잡는다 (30초에 한번만 확인)
let lastSessionCheck = 0
const checkSession = () => {
	if (Date.now() - lastSessionCheck < 30 * 1000) return
	lastSessionCheck = Date.now()

	fetchMe().catch(e => {
		if (isUnauthenticated(e)) openRelogin((e.extensions && e.extensions.reason) || 'NONE')
	})
}

// 로그인이 풀렸으면 재로그인 모달을 띄운다
// - 읽기(query): 로그인 될때까지 기다렸다가 다시 보낸다 → 요약 등 화면이 로그인 후 그대로 그려진다
// - 쓰기(mutation): 그대로 실패시키고 다시 보내지 않는다 (사용자 모르게 자동 저장 안 되게)
// 실패 처리 화면(에러 페이지, 에러 알림)은 모달이 떠 있는 동안 무시된다 (reducer/errorPage, alertSnackbar)
const errorLink = onError(({ graphQLErrors, operation, forward }) => {
	if (isRespondHost() || !graphQLErrors || !graphQLErrors.length) return
	if (SKIP_RELOGIN_OPERATIONS.includes(operation.operationName)) return

	const unauthenticated = graphQLErrors.find(c => c.extensions && c.extensions.code === 'UNAUTHENTICATED')

	if (unauthenticated) {
		openRelogin(unauthenticated.extensions.reason || 'NONE')

		const definition = getMainDefinition(operation.query)
		if (definition.kind === 'OperationDefinition' && definition.operation === 'query') {
			return fromPromise(waitForLogin()).flatMap(() => forward(operation))
		}

		return
	}

	if (getMe()) checkSession()
})

// interface 로 지정된 쿼리는 여기에 지정해줘야한다
const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: fragment
})
  
const cache = new InMemoryCache({
	fragmentMatcher
})

const client = new ApolloClient({
	link: ApolloLink.from([errorLink, authLink, splitLink]),
	cache,
	onError: ({ networkError, graphQLErrors }) => {
		console.log('graphQLErrors', graphQLErrors)
		console.log('networkError', networkError)
	}
})

const Apps = () => (
	<ApolloProvider client={client}>
		<Provider store={store}>
			<ThemeProvider theme={theme}>
				{/* 스크립트 오류(배포 후 옛 화면 파일 등)로 흰 화면 대신 새로고침 안내 */}
				<AppErrorBoundary>
					<App/>
				</AppErrorBoundary>
			</ThemeProvider>
		</Provider>
	</ApolloProvider>      
)


const container = document.getElementById('root')
const root = createRoot(container); // createRoot(container!) if you use TypeScript

// 로그인 확인(me) 후 그린다. 응답 도메인은 바로 그린다
bootstrapSession().finally(() => {
	root.render(<Apps/>)
})

serviceWorker.unregister()
